import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import { makeStyles } from "@mui/styles";
import Options from "../components/Options";
import ModalAbout from "../components/ModalAbout";
import { getTitleFromAppContent } from "../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import { trackPageView } from "../helpers/api";
import PageBackButton from "../components/PageBackButton";
import { LocationPinIcon } from "../components/LocationPinIcon";
import { changeTab } from "../redux/TabSlice";

// SVG Icon Components
const PhoneIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1468 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.09501 3.90347 2.12787 3.62476 2.2165 3.36162C2.30513 3.09849 2.44756 2.85669 2.63476 2.65162C2.82196 2.44655 3.0498 2.28271 3.30379 2.17052C3.55777 2.05833 3.83233 2.00026 4.10999 2H7.10999C7.5953 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04207 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97366 7.27691 9.8939 7.65088C9.81415 8.02485 9.62886 8.36811 9.35999 8.64L8.08999 9.91C9.51355 12.4135 11.5864 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9751 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="#7F3354" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EmailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#7F3354" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 6L12 13L2 6" stroke="#7F3354" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="#7F3354" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 6V12L16 14" stroke="#7F3354" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const WebsiteIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="#7F3354" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2 12H22" stroke="#7F3354" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="#7F3354" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const useStyles = makeStyles((theme) => ({
  bgcolor: {
    background: "#FFFFFF",
  },
  textColor: {
    color: theme.palette.primary.main,
  },
  sectionbg: {
    background: theme.palette.primary.main,
  },
  sectionBgLight: {
    backgroundColor: "#F6F1F4",
  },
  fullScreen: {
    minHeight: "100vh",
  },
  infoRow: {
    width: "500px",
    border: "1px solid #AE4971",
    display: "flex",
    alignItems: "center",
    margin: "8px 0",
    padding: "15px 20px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f9f5f7",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "calc(100vw - 24px)",
    },
  },
  infoIcon: {
    marginRight: "15px",
    flexShrink: 0,
  },
  infoContent: {
    flex: 1,
  },
  infoLabel: {
    fontSize: "12px",
    color: "#888",
    textTransform: "uppercase",
    marginBottom: "4px",
  },
  infoValue: {
    fontSize: "16px",
    color: "#333",
  },
  infoLink: {
    fontSize: "16px",
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
}));

/**
 * ContactInfo - Static contact information page
 * Shows Shirkat Gah's address, phone, email, and hours
 */
const ContactInfo = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openAbout, setOpenAbout] = useState(false);
  const { appContent } = useSelector((state) => state.appContent);
  const [contactTitle, setContactTitle] = useState("Contact Us");
  const [tabSelect, setTabSelect] = useState("ContactInfo");
  const { backFromSettings } = useSelector((state) => state.page);
  const { selLanguageId } = useSelector((state) => state.language);
  const { selProvinceId, selDistrictId } = useSelector((state) => ({ selProvinceId: state.province.selProvinceId, selDistrictId: state.district.selDistrictId }));

  const handleOpen = () => setOpenAbout(true);
  const handleClose = () => setOpenAbout(false);

  useEffect(() => {
    trackPageView("Contact Info", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [selProvinceId, selDistrictId, selLanguageId]);

  // Static contact information
  const contactInfo = {
    address: "68 Tipu Block Garden Town, Lahore, Punjab",
    phones: ["(+92-42) 35838815", "042-35836554"],
    email: "info@sgah.org.pk",
    hours: "9 am - 5 pm, Mon-Fri",
    website: "https://shirkatgah.org/",
  };

  const labelAddress = getTitleFromAppContent(appContent, "Address") || "Address";
  const labelPhone = getTitleFromAppContent(appContent, "Phone") || "Phone";
  const labelEmail = getTitleFromAppContent(appContent, "Email") || "Email";
  const labelHours = getTitleFromAppContent(appContent, "Hours") || "Hours";
  const labelWebsite = getTitleFromAppContent(appContent, "Website") || "Website";

  useEffect(() => {
    if (appContent) {
      setContactTitle(getTitleFromAppContent(appContent, "Contact Us") || "Contact Us");
    }
  }, [appContent]);

  useEffect(() => {
    dispatch(changeTab(tabSelect));
  }, [tabSelect, dispatch]);

  const handlePhoneClick = (phone) => {
    window.location.href = `tel:${phone.replace(/[^0-9+]/g, "")}`;
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${contactInfo.email}`;
  };

  const handleWebsiteClick = () => {
    window.open(contactInfo.website, "_blank");
  };

  return (
    <div className={`${classes.sectionBgLight} flex flex-col`}>
      <div>
        <Header />
        <PageBackButton
          onClick={() => {
            if (backFromSettings === "ContactInfo") setTabSelect("HumQadam");
            else setTabSelect(backFromSettings);
          }}
        />
        <Options handleOpen={handleOpen} />
        <div className="px-5 mt-2 pb-10">
          {/* Page / organization title from app content */}
          <h6
            className={`text-center mb-8 text-2xl ${classes.textColor} font-bold`}
          >
            {contactTitle}
          </h6>

          <div className="flex flex-col sm:flex-row justify-center items-center">
            <div className="flex flex-col items-center">
              {/* Address */}
              <div className={classes.infoRow}>
                <div className={classes.infoIcon}>
                  <LocationPinIcon />
                </div>
                <div className={classes.infoContent}>
                  <div className={classes.infoLabel}>{labelAddress}</div>
                  <div className={classes.infoValue}>{contactInfo.address}</div>
                </div>
              </div>

              {/* Phone */}
              <div 
                className={classes.infoRow}
                onClick={() => handlePhoneClick(contactInfo.phones[0])}
              >
                <div className={classes.infoIcon}>
                  <PhoneIcon />
                </div>
                <div className={classes.infoContent}>
                  <div className={classes.infoLabel}>{labelPhone}</div>
                  {contactInfo.phones.map((phone, index) => (
                    <div key={index} className={classes.infoLink}>
                      {phone}
                    </div>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div className={classes.infoRow} onClick={handleEmailClick}>
                <div className={classes.infoIcon}>
                  <EmailIcon />
                </div>
                <div className={classes.infoContent}>
                  <div className={classes.infoLabel}>{labelEmail}</div>
                  <div className={classes.infoLink}>{contactInfo.email}</div>
                </div>
              </div>

              {/* Hours */}
              <div className={classes.infoRow}>
                <div className={classes.infoIcon}>
                  <ClockIcon />
                </div>
                <div className={classes.infoContent}>
                  <div className={classes.infoLabel}>{labelHours}</div>
                  <div className={classes.infoValue}>{contactInfo.hours}</div>
                </div>
              </div>

              {/* Website */}
              <div className={classes.infoRow} onClick={handleWebsiteClick}>
                <div className={classes.infoIcon}>
                  <WebsiteIcon />
                </div>
                <div className={classes.infoContent}>
                  <div className={classes.infoLabel}>{labelWebsite}</div>
                  <div className={classes.infoLink}>{contactInfo.website}</div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
      <div>
        <ModalAbout open={openAbout} handleClose={handleClose} />
      </div>
    </div>
  );
};

export default ContactInfo;
